import { Typography, Skeleton, Box } from '@mui/material';
import React, { useEffect } from 'react'
import { Card, Col, Container, Row } from 'react-bootstrap';
import { AdminService } from '../../../../services/admin.service';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend } from 'recharts';

interface IPaymentLinksStats {
    dateRange?: any
}

enum PaymentStatusEnum {
    PAYMENT_COMPLETED = "completed",
    PAYMENT_PENDING = "pending",
    PAYMENT_FAILED = "failed",
    PAYMENT_PART = "partial-paid"
}

enum PaymentTypeEnum {
    NEW_STUDENT = 'New Student',
    RENEWAL = 'Renewal',
    REFERRAL = 'Referral',
    ADDITIONAL_SUBJECT = 'Additional Subject',
    VALIDITY_EXTENSION = 'Validity Extension',
}

const PaymentLinksStats = ({ dateRange }: IPaymentLinksStats) => {
    const [paymentStats, setPaymentStats] = React.useState<any[]>([]);
    const [loading, setLoading] = React.useState<boolean>(true);

    const borderColors = [
        { hex: '#28a745', rgba: 'rgba(40, 167, 69, 0.1)' },     // Green
        { hex: '#007bff', rgba: 'rgba(0, 123, 255, 0.1)' },    // Blue
        { hex: '#ffc107', rgba: 'rgba(255, 193, 7, 0.1)' },     // Yellow
        { hex: '#dc3545', rgba: 'rgba(220, 53, 69, 0.1)' },     // Red
        { hex: '#6f42c1', rgba: 'rgba(111, 66, 193, 0.1)' },    // Purple
    ];

    useEffect(() => {
        if (!dateRange?.startDate || !dateRange?.endDate) return;

        setLoading(true);
        AdminService.getPaymentLinksStats({ startDate: dateRange.startDate, endDate: dateRange.endDate })
            .then((res) => {
                if (res?.data?.stats) {
                    setPaymentStats(res.data.stats);
                } else {
                    setPaymentStats([]);
                }
            })
            .catch((err) => {
                console.error("Error fetching payment links stats", err);
                setPaymentStats([]);
            })
            .finally(() => setLoading(false));
    }, [dateRange]);

    const paymentTypeColors: Record<string, string> = {
        [PaymentTypeEnum.NEW_STUDENT]: '#4CAF50',
        [PaymentTypeEnum.RENEWAL]: '#2196F3',
        [PaymentTypeEnum.REFERRAL]: '#FFC107',
        [PaymentTypeEnum.ADDITIONAL_SUBJECT]: '#FF5722',
        [PaymentTypeEnum.VALIDITY_EXTENSION]: '#9C27B0'
    };

    const pieData = paymentStats
        .filter((stat) => stat.totalCount > 0)
        .map((stat) => ({
            name: stat.paymentType,
            value: stat.totalCount,
            color: paymentTypeColors[stat.paymentType] || '#CCCCCC'
        }));

    return (
        <Container fluid className="pt-2 pb-1">
            <h6 className="mb-4">Stats</h6>
            <Row>
                <Col xs={12} lg={7}>
                    <Row>
                        {loading
                            ? Array.from({ length: 4 }).map((_, idx) => (
                                <Col xs={12} sm={6} md={6} lg={6} className="mb-3" key={idx}>
                                    <Skeleton variant="rectangular" width="100%" height={120} animation="wave" />
                                </Col>
                            ))
                            : paymentStats.map((stat, index) => {
                                const color = borderColors[index % borderColors.length];
                                const completed = stat.statuses.find((s: any) => s.status === PaymentStatusEnum.PAYMENT_COMPLETED)?.count || 0;
                                const pending = stat.statuses.find((s: any) => s.status === PaymentStatusEnum.PAYMENT_PENDING)?.count || 0;
                                const failed = stat.statuses.find((s: any) => s.status === PaymentStatusEnum.PAYMENT_FAILED)?.count || 0;
                                const partial = stat.statuses.find((s: any) => s.status === PaymentStatusEnum.PAYMENT_PART)?.count || 0;

                                return (
                                    <Col key={index} xs={12} sm={6} md={6} lg={6} className="mb-3">
                                        <Card className="shadow-sm p-0" style={{ border: `2px solid ${color.hex}`, backgroundColor: `${color.rgba}` }}>
                                            <Card.Body>
                                                <h6>{stat.paymentType + " : " + stat.totalCount}</h6>
                                                <div>
                                                    <div className='d-flex flex-row gap-4'>
                                                        <Typography fontSize={12} fontWeight={'bold'} color={"gray"}>Completed : {completed}</Typography>
                                                        <Typography fontSize={12} fontWeight={'bold'} color={"gray"}>Pending : {pending}</Typography>
                                                    </div>
                                                    <div className='d-flex flex-row gap-4'>
                                                        <Typography fontSize={12} fontWeight={'bold'} color={"gray"}>Failed : {failed}</Typography>
                                                        <Typography fontSize={12} fontWeight={'bold'} color={"gray"}>Partial : {partial}</Typography>
                                                    </div>
                                                </div>
                                            </Card.Body>
                                        </Card>
                                    </Col>
                                );
                            })}
                    </Row>
                </Col>

                <Col xs={12} lg={5}>
                    <Card className="h-100 shadow-sm p-0">
                        <Card.Body className="d-flex flex-column justify-content-center align-items-center p-0">
                            <h6 className="mb-5">Distribution</h6>
                            <div style={{ width: '100%', height: '250px' }}>
                                {loading ? (
                                    <Box width="100%" display="flex" flexDirection="column" alignItems="center" justifyContent="center">
                                        <Skeleton variant="circular" width={120} height={120} />
                                        <Skeleton width="80%" height={20} />
                                        <Skeleton width="60%" height={20} />
                                    </Box>
                                ) : pieData.length === 0 ? (
                                    <div className="d-flex justify-content-center align-items-center h-100 w-100">
                                        <Typography fontSize={14} fontWeight="bold" color="gray">
                                            No stats to show
                                        </Typography>
                                    </div>
                                ) : (
                                    <ResponsiveContainer>
                                        <PieChart>
                                            <Pie
                                                data={pieData}
                                                dataKey="value"
                                                nameKey="name"
                                                cx="50%"
                                                cy="50%"
                                                outerRadius={80}
                                                label={({ percent, name }) =>
                                                    percent !== undefined && name !== undefined
                                                        ? `${(percent * 100).toFixed(1)}%`
                                                        : ''
                                                }
                                            >
                                                {pieData.map((entry, index) => (
                                                    <Cell key={`cell-${index}`} fill={entry.color} />
                                                ))}
                                            </Pie>
                                            <Tooltip />
                                            <Legend layout="horizontal" verticalAlign="bottom" align="center" />
                                        </PieChart>
                                    </ResponsiveContainer>
                                )}
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </Container>
    )
}

export default PaymentLinksStats;
